import { ContainerFeature, Plan } from '@back4app2/sdk/lib/Back4app2';
import { ReactComponent as Spinner } from '../assets/images/status-spinner.svg';
import { BoxIcon, DatabaseIcon } from '../components/ds/icons';
import { SegmentedControl } from '../components/ds';

/**
 * The two things that are bought on the configure step: the instance the app
 * runs on, and the extras sold per app on top of it. They land on the same
 * subscription as separate line items.
 *
 * Split in two on purpose. The app plan, the line items and the total are
 * the RAIL — pinned beside the review, because the price has to be visible
 * while the settings beside it are being read. The extras are a CARD in the
 * review column, because adding a database is a decision about the app, not a
 * number on the invoice.
 *
 * Neither renders a button: the form owns the single Create that finishes the
 * flow, and `containerTotal` is exported so the two cannot disagree about what
 * is being charged.
 */

/** Bytes to the shortest honest label. 1073741824 -> "1 GB". */
const formatStorage = (bytes?: number | null): string | null => {
  if (!bytes) {
    return null;
  }

  const gb = bytes / 1024 ** 3;
  if (gb >= 1) {
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }

  return `${Math.round(bytes / 1024 ** 2)} MB`;
};

export const formatMoney = (amount: number): string => `$${amount.toFixed(2)}`;

/**
 * What is actually billed, given a plan and the extras kept with it.
 *
 * Exported because the line that names the charge lives in the form, not here.
 * Two additions of the same numbers in two files stay equal only until one of
 * them learns about a discount.
 */
export const containerTotal = (
  plan: Plan | undefined,
  features: ContainerFeature[],
  selectedFeatureIds: string[]
): number =>
  (plan?.price || 0) +
  features
    .filter(
      (feature) =>
        selectedFeatureIds.includes(feature.id) &&
        (!plan || feature.planIds.includes(plan.id))
    )
    .reduce((sum, feature) => sum + feature.price, 0);

/**
 * A plan in one line, in the platform's own terms.
 *
 * The Free difference is that its URL EXPIRES, not that it sleeps. Both are
 * true, but they are not the same promise: sleeping is transparent — the app
 * wakes on the next request. Expiry is terminal — `handleExpiredCustomDomains`
 * disables the balancer rule, destroys the deployments and marks the domain
 * EXPIRED, and the app stays off until the user upgrades. That is the thing
 * someone choosing a plan has to know.
 *
 * It says THAT it expires, not when. `urlExpiresInMinutes` is still the
 * condition — it comes from the server, so it appears exactly on the plans that
 * really expire — but the number stays out of the line: it is a platform
 * setting that differs per environment, and one more figure next to a price and
 * two specs is a line nobody finishes reading.
 */
const planSpecs = (plan: Plan): string =>
  [
    `${plan.maxCPU} vCPU`,
    `${plan.maxRAM} MB`,
    plan.urlExpiresInMinutes ? 'URL expires' : 'always on',
  ].join('  ·  ');

/** The card's own label, in the eyebrow register the screens share. */
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="font-courrier text-ds-105 uppercase tracking-[0.08em] text-navy-300">
    {children}
  </span>
);

interface PlanOptionProps {
  plan: Plan;
  isSelected: boolean;
  onSelect: () => void;
  /** Shown inside the option, and only while it is the selected one. */
  blockedMessage?: string;
}

/**
 * Compact by design: in the rail there is no room for a feature table, and
 * there is nothing to compare anyway — the plans differ by two numbers and one
 * promise, which is exactly what the line under the name says.
 */
const PlanOption = ({ plan, isSelected, onSelect, blockedMessage }: PlanOptionProps) => (
  <label
    className={`block px-4 py-3 rounded-card border cursor-pointer transition-colors duration-[120ms] ${
      isSelected
        ? 'bg-action/[0.10] border-action'
        : 'bg-navy-700 border-transparent hover:border-navy-400'
    }`}
  >
    {/* The real control, visually hidden rather than removed: it keeps the
        group keyboard-navigable and the label clickable. */}
    <input
      type="radio"
      name="container-plan"
      className="sr-only"
      checked={isSelected}
      onChange={onSelect}
    />
    <span className="flex items-baseline justify-between gap-3">
      <span className="font-sora text-ds-135 font-semibold text-white">
        {plan.name}
      </span>
      <span className="font-sora text-ds-135 font-semibold text-white">
        {plan.price === 0 ? '$0' : `$${plan.price}`}
      </span>
    </span>
    <span className="mt-1 block font-courrier text-ds-115 text-navy-200">
      {planSpecs(plan)}
    </span>

    {/* Inside the option, under what it is about — not beside the button,
        where it would read as a complaint about the whole form rather than
        about the plan that causes it. */}
    {isSelected && blockedMessage ? (
      <span className="mt-2 block text-ds-115 leading-[1.5] text-state-warn">
        {blockedMessage}
      </span>
    ) : null}
  </label>
);

export interface AppPlanPanelProps {
  plans: Plan[];
  features: ContainerFeature[];
  selectedPlanId?: string;
  selectedFeatureIds: string[];
  onSelectPlan: (planId: string) => void;
  /** The catalog is fetched when the step opens; until it lands there is
   * nothing to choose between, and empty rows would read as "no plans". */
  isLoading?: boolean;
  errorMessage?: string;
  /**
   * The account's free allowance, from the server. When it is spent, the Free
   * option says so — and only the Free option: a paid plan does not consume a
   * free slot, so warning about it anywhere else would be warning about a limit
   * that does not apply to what is being bought.
   */
  freeAppsUsed?: number;
  maxFreeApps?: number | null;
  /**
   * The name the app is being created under, so the first line of the bill
   * names the thing being bought rather than the plan it runs on.
   *
   * Reported up from the form, where the field lives.
   */
  appName?: string;
}

export const AppPlanPanel = ({
  plans,
  features,
  selectedPlanId,
  selectedFeatureIds,
  onSelectPlan,
  isLoading = false,
  errorMessage,
  freeAppsUsed,
  maxFreeApps,
  appName,
}: AppPlanPanelProps) => {
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);

  const chosenFeatures = features.filter(
    (feature) =>
      selectedFeatureIds.includes(feature.id) &&
      (!selectedPlan || feature.planIds.includes(selectedPlan.id))
  );

  const total = containerTotal(selectedPlan, features, selectedFeatureIds);

  // Only a free plan consumes a free slot, so this is the only option it can be
  // about. `>=` and not `===`: an account can end up over the limit — a paid app
  // that goes back to Free, or a limit that was lowered — and it must still say
  // the allowance is spent rather than go quiet.
  const freeAllowanceMessage =
    typeof maxFreeApps === 'number' &&
    typeof freeAppsUsed === 'number' &&
    freeAppsUsed >= maxFreeApps
      ? `You're using all ${maxFreeApps} of your free web apps. Delete one, or move an app to a paid plan, to deploy another.`
      : undefined;

  if (isLoading) {
    return (
      <div className="px-5 py-16 flex items-center justify-center">
        <Spinner className="animate-spin" width="20px" height="20px" />
      </div>
    );
  }

  /**
   * At most two plans reach the create form — `offered_at_creation` is a
   * column, and production carries Free and Starter — so the choice is a
   * segmented control rather than a stack of cards: two panes the width of the
   * rail, for a choice between two words.
   *
   * It is a column, not a hardcoded pair. The flag exists so the offer can
   * change without a deploy, and a third plan turns a segmented control into a
   * design problem — so above two it falls back to the cards, which take any
   * number.
   */
  const asSegments = plans.length > 0 && plans.length <= 2;

  /**
   * A plan that cannot be chosen while an extra that it does not sell is on.
   *
   * Selecting it used to be allowed and DROPPED the extra — quietly, in the
   * reducer, with the rail simply showing one line fewer. Refusing the segment
   * and saying which extra is in the way leaves the choice with the person who
   * made it: turn the database off, then move to Free.
   */
  const blockingFeatureFor = (plan: Plan): ContainerFeature | undefined =>
    features.find(
      (feature) =>
        selectedFeatureIds.includes(feature.id) &&
        !feature.planIds.includes(plan.id)
    );

  const blockedPlanMessage = (() => {
    if (!asSegments) {
      return undefined;
    }

    for (const plan of plans) {
      const blocker = blockingFeatureFor(plan);

      if (blocker) {
        return `${plan.name} isn't available with a ${blocker.kind}.`;
      }
    }

    return undefined;
  })();

  return (
    <div>
      {/* A bill says what it is a bill for. The rows below name the app and
          the extras, never the thing being chosen, so without this the card
          opens on a name and a price with nothing saying they are a plan. */}
      <div className="px-5 pt-4 pb-3 border-b border-navy-600">
        <SectionLabel>app plan</SectionLabel>
      </div>

      {/* The bill, one row per thing being bought. The app and its extras were
          two objects before — a plan panel here and a Database card at the foot
          of the review column — which asked the reader to add up a total from
          two places while the total was printed in one of them. */}
      <div className="px-5 pt-4 pb-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <span className="flex-none w-9 h-9 rounded-control bg-action/[0.14] text-action-soft flex items-center justify-center">
            <BoxIcon size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate font-sora text-ds-135 font-semibold text-white">
                {appName || 'Your app'}
              </span>
              <span className="flex-none font-sora text-ds-135 font-semibold text-white">
                {selectedPlan ? formatMoney(selectedPlan.price) : '\u2014'}
              </span>
            </div>
          </div>
        </div>

        {asSegments ? (
          <SegmentedControl
            size="touch"
            ariaLabel="App plan"
            className="w-full [&>button]:flex-1"
            value={selectedPlanId || plans[0].id}
            onChange={onSelectPlan}
            segments={plans.map((plan) => ({
              value: plan.id,
              label: plan.name,
              isDisabled: !!blockingFeatureFor(plan),
            }))}
          />
        ) : (
          <div className="flex flex-col gap-2">
            {plans.map((plan) => (
              <PlanOption
                key={plan.id}
                plan={plan}
                isSelected={plan.id === selectedPlanId}
                onSelect={() => onSelectPlan(plan.id)}
                blockedMessage={plan.price === 0 ? freeAllowanceMessage : undefined}
              />
            ))}
          </div>
        )}

        {/* What the chosen plan gives, in the platform's own terms. Under the
            control rather than inside each segment: a segment is one word
            wide. */}
        {asSegments && selectedPlan ? (
          <span className="block font-courrier text-ds-115 text-navy-200">
            {planSpecs(selectedPlan)}
          </span>
        ) : null}

        {/* Why a segment will not depress, and — separately — why Free may not
            be usable even when it will. Both are about the plan row, so both
            live under it. */}
        {blockedPlanMessage ? (
          <span className="block text-ds-115 leading-[1.5] text-navy-200">
            {blockedPlanMessage}
          </span>
        ) : null}
        {asSegments && selectedPlan?.price === 0 && freeAllowanceMessage ? (
          <span className="block text-ds-115 leading-[1.5] text-state-warn">
            {freeAllowanceMessage}
          </span>
        ) : null}
      </div>

      {/* Extras as siblings of the app, not as a list of amounts: same icon,
          same name-and-price shape, because on the invoice they are the same
          kind of thing. */}
      {chosenFeatures.map((feature) => {
        const storage = formatStorage(feature.maxStorageBytes);

        return (
          <div
            key={feature.id}
            className="px-5 py-4 border-t border-navy-600 flex items-start gap-3"
          >
            <span className="flex-none w-9 h-9 rounded-control bg-action/[0.14] text-action-soft flex items-center justify-center">
              <DatabaseIcon size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate font-sora text-ds-135 font-semibold text-white">
                  {feature.name.replace(/\s+database$/i, '')}
                </span>
                <span className="flex-none font-sora text-ds-135 font-semibold text-white">
                  {formatMoney(feature.price)}
                </span>
              </div>
              {storage ? (
                <span className="mt-0.5 block font-courrier text-ds-115 text-navy-200">
                  {storage}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}

      <div className="px-5 py-4 border-t border-navy-600 flex items-baseline justify-between gap-3">
        <span className="text-ds-135 text-white">Total</span>
        <span className="flex items-baseline gap-1.5">
          <span className="font-sora text-[26px] leading-none font-semibold text-white">
            {formatMoney(total)}
          </span>
          <span className="text-ds-115 text-navy-200">/ month</span>
        </span>
      </div>

      {errorMessage ? (
        <p className="m-0 px-5 pb-4 text-ds-115 text-state-err">{errorMessage}</p>
      ) : null}
    </div>
  );
};

export interface FeaturesCardProps {
  features: ContainerFeature[];
  plans: Plan[];
  selectedPlanId?: string;
  selectedFeatureIds: string[];
  onToggleFeature: (featureId: string) => void;
  isLoading?: boolean;
  /**
   * The variable a bought database is handed to the app through.
   *
   * Only read while the extra is on, and only for a database: it is the one
   * thing about a switched-on extra that is not already on the row, and
   * without it "on" says nothing about what changed in the app.
   */
  connectedViaEnvKey?: string;
}

/**
 * The extras, as the last card of the review.
 *
 * Shown, and addable, even when the chosen plan cannot buy them: Add then
 * moves the app to the cheapest plan that sells the extra, and a quiet line
 * says so beforehand. Hiding or locking them while Free is selected made the
 * person who came here to add a database find the plan that unlocks it on
 * their own.
 */
export const FeaturesCard = ({
  features,
  plans,
  selectedPlanId,
  selectedFeatureIds,
  onToggleFeature,
  isLoading = false,
  connectedViaEnvKey,
}: FeaturesCardProps) => {
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);

  if (isLoading || !features.length) {
    return null;
  }

  /**
   * "Features" is what this card is called when it holds several kinds of
   * thing. It holds one kind — a database — and a heading that names a
   * category over a list of one made the reader work out which category the
   * one row was in.
   *
   * Derived rather than hardcoded, and the list is not filtered to match. A
   * second extra of another kind is a catalog row away, and a card that
   * silently dropped it would be a thing the platform sells and nobody can
   * buy. When that day comes the heading goes back to "Features" on its own.
   *
   * Branching on `kind`, never on `key` or `name` — the catalog is editable.
   */
  const title = features.every((feature) => feature.kind === 'database')
    ? 'Database'
    : 'Features';

  return (
    <section className="rounded-card bg-navy-800 border border-navy-500 overflow-hidden">
      {/* Nothing on the right any more. It read "Billed with your plan" or
          "Available on paid plans" — the first says what a price beside a
          switch already says, and the second is a rule about one row, which
          that row now states itself. */}
      <header className="px-5 py-3.5 border-b border-navy-600">
        <h2 className="m-0 font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
          {title}
        </h2>
      </header>

      <div className="px-5">
        {features.map((feature) => {
          // The catalog carries the gate; the purchase path re-checks it
          // server-side — this is a courtesy, not a control.
          const isAvailable =
            !selectedPlan || feature.planIds.includes(selectedPlan.id);
          const isSelected = selectedFeatureIds.includes(feature.id);
          const storage = formatStorage(feature.maxStorageBytes);
          // The cheapest paid plan this extra is sold on — the plan it
          // belongs to. Worked out whether or not it is the one selected,
          // because the row names it while the switch is off either way: as a
          // requirement already met, or as where turning the switch on takes
          // the app.
          const sellingPlan = plans
            .filter((plan) => plan.price > 0 && feature.planIds.includes(plan.id))
            .sort((a, b) => a.price - b.price)[0];

          // Where the switch takes the plan when the chosen one does not sell
          // this. Undefined when it already does, and when nothing does.
          const upgradePlan = isAvailable ? undefined : sellingPlan;

          // The card is called Database; the row does not repeat it. The
          // catalog name is "PostgreSQL Database" because it also appears
          // where no heading stands over it — an invoice line, the app's own
          // Features list — so the word is trimmed here rather than taken out
          // of the catalog.
          const name = title === 'Database'
            ? feature.name.replace(/\s+database$/i, '')
            : feature.name;

          // Two states, two sentences.
          //
          // Off, the row names the plan the extra is sold on — the same words
          // whether that plan is the one selected or not. On Free it is what
          // flipping the switch will select; on Starter it is a requirement
          // already met. The alternative was a line that changed wording with
          // the plan, so the row read differently depending on something the
          // rail above it had already said.
          //
          // On, it names the variable the database arrives as, which is the
          // whole of how the code reaches it and the one thing about a
          // switched-on extra that the row does not otherwise carry.
          const databaseKey =
            feature.kind === 'database' && connectedViaEnvKey
              ? connectedViaEnvKey
              : undefined;

          const note = isSelected && databaseKey
            ? (
              <>
                Connected via{' '}
                <span className="font-courrier text-navy-200">{databaseKey}</span>
              </>
            )
            : sellingPlan
            ? `Requires ${sellingPlan.name} plan`
            // Nothing sells it. There is nowhere for the switch to take the
            // app, and it is disabled too.
            : 'Not available on this plan';

          const canToggle = isAvailable || !!upgradePlan;

          return (
            <div
              key={feature.id}
              className={`py-4 flex items-center gap-3.5 border-b border-navy-700 last:border-b-0 ${
                canToggle ? '' : 'opacity-50'
              }`}
            >
              {/* The extra's own mark, so the row is recognisable before it is
                  read — the same move the build method rows make. */}
              <span className="flex-none w-9 h-9 rounded-control bg-action/[0.14] text-action-soft flex items-center justify-center">
                <DatabaseIcon size={18} />
              </span>

              <div className="min-w-0 flex-1">
                {/* Wraps rather than truncates. On a phone the icon, price and
                    switch leave the name about 130px, and "PostgreS…" was the
                    row's only word for what it sells. */}
                <div className="font-sora text-ds-135 font-semibold text-white break-words">
                  {name}
                  {storage ? <span className="text-navy-200"> · {storage}</span> : null}
                </div>
                {/* Below `sm` the price sits under the name instead of in a
                    column of its own — that column is the width the name was
                    missing. */}
                <div className="sm:hidden mt-0.5 font-courrier text-ds-125 text-navy-200">
                  +${feature.price}/mo
                </div>
                {note ? (
                  <div className="mt-0.5 text-ds-115 leading-[1.5] text-navy-300">
                    {note}
                  </div>
                ) : null}
              </div>

              <span className="hidden sm:inline flex-none font-courrier text-ds-125 text-navy-200">
                +${feature.price}/mo
              </span>

              {/* A switch, not Add/Remove. The row is a thing that is on or
                  off; two labels for the two states made the control read as
                  an action with consequences to work out first.

                  Same 46×26 as ToggleSwitchCheckbox, written here because that
                  component owns a label-and-hint layout this row does not have
                  — the icon, the price and the switch are the row. */}
              <label
                className={`relative flex-none w-[46px] h-[26px] rounded-full border p-0.5 flex items-center transition-colors duration-[120ms] ${
                  canToggle ? 'cursor-pointer' : 'cursor-not-allowed'
                } ${isSelected ? 'justify-end bg-action border-action' : 'justify-start bg-navy-600 border-navy-400'}`}
              >
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isSelected}
                  onChange={() => onToggleFeature(feature.id)}
                  disabled={!canToggle}
                  role="switch"
                  aria-label={`${name}, +$${feature.price} per month`}
                />
                <span className="w-5 h-5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.4)] block peer-focus-visible:ring-2 peer-focus-visible:ring-action peer-focus-visible:ring-offset-1" />
              </label>
            </div>
          );
        })}
      </div>
    </section>
  );
};
